
import { supabase } from '@/integrations/supabase/client';
import type { InsightCard } from '@/types/insight'; 
import { transformDatabaseCard } from '@/types/insight';

// Define a specific interface for options to avoid infinite type instantiation
export interface FetchInsightsOptions {
  countryCode?: string;
  constituencyCode?: string;
  categoryId?: string;
  status?: string;
  searchQuery?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Fetch insight cards with pagination and filtering
 */
export const fetchInsights = async (options: FetchInsightsOptions = {}) => {
  try {
    const {
      countryCode,
      constituencyCode,
      categoryId,
      status = 'published',
      searchQuery,
      page = 1,
      pageSize = 10,
      sortBy = 'created_at',
      sortOrder = 'desc'
    } = options;
    
    // Calculate pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('insight_cards')
      .select('*', { count: 'exact' });
    
    // Apply filters
    if (countryCode) {
      query = query.eq('country_code', countryCode);
    }
    
    if (constituencyCode) {
      query = query.eq('constituency_code', constituencyCode);
    }
    
    if (categoryId) {
      query = query.eq('category_id', categoryId);
    }
    
    if (status) {
      query = query.eq('status', status);
    }
    
    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
    }
    
    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });
    
    // Apply pagination
    query = query.range(from, to);
    
    const { data, count, error } = await query;
    
    if (error) {
      throw error;
    }
    
    // Transform database format to InsightCard format using the transformer
    const transformedData = (data || []).map(transformDatabaseCard);
    
    return { 
      data: transformedData, 
      count: count || 0, 
      page, 
      pageSize 
    };
  } catch (error) {
    console.error('Error fetching insights:', error);
    throw error;
  }
};

/**
 * Fetch a single insight card by ID
 */
export const fetchInsightById = async (id: string): Promise<InsightCard | null> => {
  try {
    const { data, error } = await supabase
      .from('insight_cards')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      throw error;
    }
    
    // Transform database format to InsightCard format
    return data ? transformDatabaseCard(data) : null;
  } catch (error) {
    console.error('Error fetching insight by ID:', error);
    return null;
  }
};

/**
 * Fetch a single insight card by slug
 */
export const fetchInsightBySlug = async (slug: string): Promise<InsightCard | null> => {
  try {
    const { data, error } = await supabase
      .from('insight_cards')
      .select('*')
      .eq('slug', slug)
      .single();
    
    if (error) {
      throw error;
    }
    
    // Transform database format to InsightCard format
    return data ? transformDatabaseCard(data) : null;
  } catch (error) {
    console.error('Error fetching insight by slug:', error);
    return null;
  }
};

/**
 * Export fetchInsightCardBySlug to maintain compatibility with existing code
 * This function is used in src/pages/InsightPage.tsx
 */
export const fetchInsightCardBySlug = fetchInsightBySlug;
